import { Divider, Typography } from '@mui/material';
import { Column, Row } from 'components/Layout';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Card, CardContent } from 'further-ui/components/Card';
import TooltipTypograhy from 'further-ui/components/TooltipTypography';
import { numberToCurrencyString } from 'further-ui/utils';
import { round } from 'lodash';

type Props = {
  saleAmount: number;
  purchaseAmount: number;
  rootCurrency: CurrencyCode;
  saleRate: number;
  purchaseRate: number;
  foreignCurrencyGain: number;
};

const tooltipText =
  "To calculate the cost of foreign currency, the Further system uses the average cost method. This method smooths out fluctuations in the exchange rate by calculating the average rate at which the relevant currency was acquired over the vehicle's lifetime. This method provides a balanced approach, mitigating the effects of short-term exchange rate volatility in reporting the cost of foreign currency for a particular transaction.";

const CurrencySaleReview: React.FC<Props> = ({
  rootCurrency,
  saleAmount,
  purchaseAmount,
  saleRate,
  purchaseRate,
  foreignCurrencyGain,
}) => {
  const costsAmount = purchaseRate ? round(saleAmount / purchaseRate, 2) : 0;

  const summaryAmountText = numberToCurrencyString(
    Math.abs(foreignCurrencyGain),
    {
      currency: rootCurrency,
    },
  );

  return (
    <Column fullWidth spacing="sm" centered>
      <Card variant="outlined">
        <CardContent size="sm">
          <Column fullWidth spacing="sm">
            <Column spacing="xs">
              <Row centered fullWidth justify="space-between" spacing="sm">
                <Typography variant="body2">
                  Sale value of foreign currency in {rootCurrency} (FX sale
                  rate: {round(saleRate, 6).toFixed(6)})
                </Typography>
                <Typography variant="body2">
                  {numberToCurrencyString(purchaseAmount, {
                    currency: rootCurrency,
                  })}
                </Typography>
              </Row>
              <Row centered fullWidth justify="space-between" spacing="sm">
                <Typography variant="body2">
                  Cost of foreign currency in {rootCurrency} (average FX
                  purchase rate: {round(purchaseRate, 6).toFixed(6)})
                </Typography>
                <Typography variant="body2">
                  {numberToCurrencyString(costsAmount, {
                    currency: rootCurrency,
                  })}
                </Typography>
              </Row>
            </Column>

            <Divider />
            <Row centered fullWidth justify="space-between" spacing="sm">
              <Typography variant="h6" fontWeight={700}>
                Foreign currency gain/(loss)
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {foreignCurrencyGain < 0
                  ? `(${summaryAmountText})`
                  : summaryAmountText}
              </Typography>
            </Row>
          </Column>
        </CardContent>
      </Card>
      <Row fullWidth>
        <TooltipTypograhy
          variant="body2"
          textAlign="left"
          tooltip={tooltipText}
        >
          How is the average FX purchase rate calculated?
        </TooltipTypograhy>
      </Row>
    </Column>
  );
};

export default CurrencySaleReview;
